"use client";

import React, { useState, useEffect, useRef } from "react";
import { Phone, ShieldCheck, CircleCheck as CheckCircle2, Clock, Loader } from 'lucide-react';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const trustBadges = [{
  title: "Vetted Local Pros",
  desc: "Independent contractors verified for appliance repair across metro Atlanta.",
  icon: ShieldCheck
}, {
  title: "Direct Pro Quotes",
  desc: "Pros evaluate your unit and provide pricing before work begins.",
  icon: CheckCircle2
}, {
  title: "Prompt Local Matching",
  desc: "Connect with available Atlanta technicians for your service needs.",
  icon: Clock
}];
const DEFAULT_APPLIANCE_OPTIONS = [{
  value: "refrigerator",
  label: "Refrigerator Repair"
}, {
  value: "washer",
  label: "Washer Repair"
}, {
  value: "dryer",
  label: "Dryer Repair"
}, {
  value: "dishwasher",
  label: "Dishwasher Repair"
}, {
  value: "oven_stove",
  label: "Oven & Stove Repair"
}, {
  value: "microwave",
  label: "Microwave Repair"
}, {
  value: "garbage_disposal",
  label: "Garbage Disposal Repair"
}, {
  value: "freezer",
  label: "Freezer Repair"
}, {
  value: "other",
  label: "Other Appliance"
}];
function BookingFormFields() {
  const [formDef, setFormDef] = useState<any>(null);
  const [loadingForm, setLoadingForm] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [applianceType, setApplianceType] = useState("");
  const [message, setMessage] = useState("");
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const fetchTimestampRef = useRef<number>(Date.now());
  const fetchDefinition = async () => {
    const client = getWvcClient();
    if (!client) {
      setLoadError("Form service is unavailable.");
      setLoadingForm(false);
      return;
    }
    try {
      const def = await client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request");
      setFormDef(def);
      fetchTimestampRef.current = Date.now();
      setLoadingForm(false);
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items = listRes?.items || [];
          const matched = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : null) || items.find((f: any) => /homeowner|service|repair/i.test(f.name || ""));
          if (matched?.key) {
            const fallbackDef = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            setFormDef(fallbackDef);
            fetchTimestampRef.current = Date.now();
            setLoadingForm(false);
            return;
          }
        } catch {
          // continue to load error
        }
      }
      setLoadError("Unable to load booking form. Please call us directly.");
      setLoadingForm(false);
    }
  };
  useEffect(() => {
    fetchDefinition();
  }, []);
  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPhone("");
    setApplianceType("");
    setMessage("");
    setHpValue("");
    setFieldErrors({});
    setGeneralError(null);
    setIsSubmitted(false);
    setConfirmationMessage("");
    fetchDefinition();
  };
  if (loadingForm) {
    return <div className="flex flex-col items-center justify-center py-12 space-y-3 text-muted-foreground">
        <Loader className="size-6 animate-spin text-primary" />
        <p className="text-sm">Loading service request form...</p>
      </div>;
  }
  if (loadError || !formDef) {
    return <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-6 text-center space-y-3 text-card-foreground">
        <p className="text-sm text-destructive font-medium">
          {loadError || "Unable to load the service request form at this time."}
        </p>
        <p className="text-xs text-muted-foreground">
          Please contact support directly at (470) 847-9076 for immediate assistance.
        </p>
      </div>;
  }
  if (isSubmitted) {
    return <div className="rounded-xl bg-card p-6 border border-border text-card-foreground text-center space-y-4">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="font-serif text-xl font-bold">Booking Request Received</h3>
        {confirmationMessage ? <div className="text-sm text-muted-foreground space-y-2 [&_p]:text-muted-foreground" dangerouslySetInnerHTML={{
        __html: confirmationMessage
      }} /> : <p className="text-sm text-muted-foreground">
            Thank you! Your booking request has been submitted. A specialist will call you shortly.
          </p>}
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-lg">
          Submit Another Request
        </Button>
      </div>;
  }
  const fields = formDef.fields || [];
  const fieldFullName = fields.find((f: any) => f.key === "full_name");
  const fieldEmail = fields.find((f: any) => f.key === "email");
  const fieldPhone = fields.find((f: any) => f.key === "phone");
  const fieldApplianceType = fields.find((f: any) => f.key === "appliance_type");
  const fieldMessage = fields.find((f: any) => f.key === "message");
  const fieldSourcePage = fields.find((f: any) => f.key === "source_page");
  const fieldSourceCity = fields.find((f: any) => f.key === "source_city");
  const fieldSourceService = fields.find((f: any) => f.key === "source_service");
  const applianceOptions = fieldApplianceType?.options && fieldApplianceType.options.length > 0 ? fieldApplianceType.options : DEFAULT_APPLIANCE_OPTIONS;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    const client = getWvcClient();
    if (!client) {
      setGeneralError("Form submission service is currently unavailable.");
      return;
    }
    const errors: Record<string, string> = {};
    if (fieldFullName && fieldFullName.required && !fullName.trim()) {
      errors[fieldFullName.id] = fieldFullName.validation?.messages?.required || "Full name is required";
    }
    if (fieldEmail && fieldEmail.required && !email.trim()) {
      errors[fieldEmail.id] = fieldEmail.validation?.messages?.required || "Email address is required";
    }
    if (fieldPhone && fieldPhone.required && !phone.trim()) {
      errors[fieldPhone.id] = fieldPhone.validation?.messages?.required || "Phone number is required";
    }
    if (fieldApplianceType && fieldApplianceType.required && !applianceType) {
      errors[fieldApplianceType.id] = fieldApplianceType.validation?.messages?.required || "Please select an appliance type";
    }
    if (fieldMessage && fieldMessage.required && !message.trim()) {
      errors[fieldMessage.id] = fieldMessage.validation?.messages?.required || "Please provide details about your request";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setIsSubmitting(true);
    const minSeconds = formDef.spam?.min_seconds ?? 3;
    const elapsedSeconds = (Date.now() - fetchTimestampRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitTime = Math.ceil((minSeconds - elapsedSeconds) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    const values: Record<string, any> = {};
    if (fieldFullName) values[fieldFullName.id] = fullName.trim();
    if (fieldEmail) values[fieldEmail.id] = email.trim();
    if (fieldPhone) values[fieldPhone.id] = phone.trim();
    if (fieldApplianceType && applianceType) values[fieldApplianceType.id] = applianceType;
    if (fieldMessage && message.trim()) values[fieldMessage.id] = message.trim();
    if (fieldSourcePage) values[fieldSourcePage.id] = "Appliance Repair in Atlanta GA";
    if (fieldSourceCity) values[fieldSourceCity.id] = "Atlanta";
    if (fieldSourceService) values[fieldSourceService.id] = "Appliance Repair";
    fields.forEach((f: any) => {
      if (f.type === "hidden" && !(f.id in values) && f.default) {
        values[f.id] = f.default;
      }
    });
    const hpPayload = formDef.spam?.honeypot?.field ? {
      [formDef.spam.honeypot.field]: hpValue
    } : undefined;
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: {
          values,
          token: formDef.spam?.token,
          hp: hpPayload,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        setConfirmationMessage(res.confirmation?.message || "Thank you! Your booking request has been submitted. A specialist will call you shortly.");
        setIsSubmitted(true);
      } else if (res?.code === "frms_validation_failed" && res?.errors) {
        const mappedErrors: Record<string, string> = {};
        Object.entries(res.errors).forEach(([fid, errObj]: [string, any]) => {
          mappedErrors[fid] = errObj?.message || "Invalid value";
        });
        setFieldErrors(mappedErrors);
        setGeneralError(res.message || "Please check the highlighted fields.");
      } else if (res?.code === "frms_invalid_token") {
        await fetchDefinition();
        setGeneralError("Your session expired. Please submit the form again.");
      } else {
        setGeneralError(res?.message || "Failed to submit your request. Please call us directly.");
      }
    } catch (err: any) {
      setGeneralError(err?.message || "Failed to submit your request. Please call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-4">
      {formDef.spam?.honeypot?.field && <div style={{
      position: "absolute",
      left: "-9999px",
      opacity: 0
    }} aria-hidden="true">
          <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
        </div>}

      {fieldFullName && <div className="space-y-1.5">
          <Label htmlFor="full_name" className="text-sm font-medium">
            Name {fieldFullName.required && <span className="text-primary">*</span>}
          </Label>
          <Input id="full_name" type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Doe" className="bg-background" aria-invalid={!!fieldErrors[fieldFullName.id]} />
          {fieldErrors[fieldFullName.id] && <p className="text-xs text-destructive">{fieldErrors[fieldFullName.id]}</p>}
        </div>}

      {fieldEmail && <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">
            Email {fieldEmail.required && <span className="text-primary">*</span>}
          </Label>
          <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="bg-background" aria-invalid={!!fieldErrors[fieldEmail.id]} />
          {fieldErrors[fieldEmail.id] && <p className="text-xs text-destructive">{fieldErrors[fieldEmail.id]}</p>}
        </div>}

      {fieldPhone && <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-sm font-medium">
            Phone {fieldPhone.required && <span className="text-primary">*</span>}
          </Label>
          <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" className="bg-background" aria-invalid={!!fieldErrors[fieldPhone.id]} />
          {fieldErrors[fieldPhone.id] && <p className="text-xs text-destructive">{fieldErrors[fieldPhone.id]}</p>}
        </div>}

      {fieldApplianceType && <div className="space-y-1.5">
          <Label htmlFor="appliance_type" className="text-sm font-medium">
            Service Type {fieldApplianceType.required && <span className="text-primary">*</span>}
          </Label>
          <Select value={applianceType} onValueChange={setApplianceType}>
            <SelectTrigger id="appliance_type" className="w-full bg-background" aria-invalid={!!fieldErrors[fieldApplianceType.id]}>
              <SelectValue placeholder="Select service required..." />
            </SelectTrigger>
            <SelectContent>
              {applianceOptions.map((opt: any) => <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>)}
            </SelectContent>
          </Select>
          {fieldErrors[fieldApplianceType.id] && <p className="text-xs text-destructive">{fieldErrors[fieldApplianceType.id]}</p>}
        </div>}

      {fieldMessage && <div className="space-y-1.5">
          <Label htmlFor="message" className="text-sm font-medium">
            Additional Notes {fieldMessage.required && <span className="text-primary">*</span>}
          </Label>
          <Textarea id="message" value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your issue or any access instructions..." className="bg-background min-h-24" aria-invalid={!!fieldErrors[fieldMessage.id]} />
          {fieldErrors[fieldMessage.id] && <p className="text-xs text-destructive">{fieldErrors[fieldMessage.id]}</p>}
        </div>}

      {generalError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {generalError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 min-h-[48px] text-base font-medium shadow-md transition-all mt-2">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : "Submit Service Request"}
      </Button>
    </form>;
}
export default function Bookingform() {
  return <section id="bookingform" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-6 space-y-8">
            <div className="space-y-4">
              <span className="text-primary text-xs sm:text-sm font-semibold tracking-wider uppercase bg-primary/10 px-3 py-1 rounded-lg inline-block">
                Schedule Service
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground">
                Request an Atlanta Appliance Pro
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Connect directly with independent local technicians in metro Atlanta for refrigerator, washer, dryer, oven, and dishwasher repair. Submit your request below or call Handy Heroes at (470) 847-9076.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-card border border-border text-card-foreground shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Prefer to Call Directly?
                </p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  (470) 847-9076
                </p>
                <p className="text-xs text-muted-foreground">Call to speak with our Atlanta support team</p>
              </div>
              <a href="tel:14708479076" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors shadow-xs">
                <Phone className="size-4" />
                Call Now
              </a>
            </div>

            <div className="space-y-4 pt-2">
              {trustBadges.map((badge, i) => {
              const IconComponent = badge.icon;
              return <div key={badge.title} data-index={i} className="flex items-start gap-4 p-3 rounded-lg hover:bg-card/50 transition-colors">
                    <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0 mt-1">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-base">{badge.title}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{badge.desc}</p>
                    </div>
                  </div>;
            })}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-6">
            <Card className="bg-card text-card-foreground border border-border shadow-md rounded-2xl p-2 sm:p-4">
              <CardHeader className="space-y-1.5 pb-4">
                <CardTitle className="font-serif text-2xl font-bold">Booking Request Form</CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Select your service requirements and preferred arrival window.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BookingFormFields />
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}